/**
 * @file Postgrest.js.
 * @author Sérgio Ferraz Souza <todentro@gmail.com>
 * @copyright Cognatis GeoAnalytics & Big Data Ltda :: 2020
 */

import Service from './Service'
import Request from '../components/http/request'

const defaultJsonHeaders = {
  headers: {
    'Content-Type': 'application/json'
  }
}

/**
 * Postgrest Management Service
 * @class
 */
class Postgrest extends Service {
  constructor (options, resource) {
    super(options)
    this.resource = resource
    this.request = new Request({tag: 'das'})
  }
  /**
   * Execute a url
   *
   * @async
   * @function executeCommand
   * @param {Object} command - Sql command to execute
   * @return {Promise<json>} The json with the result
   */
  async executeCommand (command) {
    return new Promise((resolve, reject) => {
      if (typeof command === 'undefined') reject(new Error('Esperada a instrução sql'))
      const endPoint = '/api/0.1-a/ProxyPostgRest/api/rpc/command'
      const jsonHeaders = {
        headers: {
          'Content-Type': 'application/json',
          'Prefer': 'count=exact'
        }
      }
      this.request.post(endPoint, command, jsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Execute a url
   *
   * @async
   * @function fullTextSearch
   * @param {string} url - Url to execute
   * @param {Object} data - Data to send
   * @return {Promise<json>} The json with the result
   */
  async executeUrl (url, data = null) {
    return new Promise((resolve, reject) => {
      if (typeof url === 'undefined') reject(new Error('Esperada a url'))
      this.request.post(url, data, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Execute a url com método GET
   *
   * @async
   * @function
   * @param {string} url - Url to execute
   * @return {Promise<json>} The json with the result
   */
  async executeUrlGet (url, headers = defaultJsonHeaders) {
    return new Promise((resolve, reject) => {
      if (typeof url === 'undefined') reject(new Error('Esperada a url'))
      // const req = new Request({tag: 'postgrest'})
      this.request.get(url, headers)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Execute a url direto ao PostgRest
   *
   * @async
   * @function executeGet
   * @param {string} url - Url to execute
   * @return {Promise<json>} The json with the result
   */
  async executeGet (url, headers = defaultJsonHeaders) {
    return new Promise((resolve, reject) => {
      if (typeof url === 'undefined') reject(new Error('Esperada a url'))
      const req = new Request({tag: 'postgrest'})
      req.get(url, headers)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Fire a full text searching
   *
   * @async
   * @function fullTextSearch
   * @param {string} table - Table name
   * @param {string} column - Column name
   * @param {string} input - Input text
   * @return {Promise<json>} The json with the result
   */
  async fullTextSearch (table, column, input) {
    return new Promise((resolve, reject) => {
      if (typeof table === 'undefined') reject(new Error('Esperado o nome da tabela'))
      if (typeof column === 'undefined') reject(new Error('Esperado o nome da coluna'))
      if (typeof input === 'undefined') reject(new Error('Esperado o texto de entrada'))
      const endPoint = `/api/0.1-a/ProxyPostgRest/api/${table}?${column}=plfts.${input}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get data
   *
   * @async
   * @function get
   * @param {string} table - Table name
   * @param {string} columns - Columns list separated by comma
   * @param {Object} filter - Filter expression
   * @param {Object} order - Order by expression
   * @param {Number} skip - Lines to skip
   * @param {Number} take - Lines to take
   * @return {Promise<json>} The json with the result
   */
  async get (table, columns = '*', filter = null, order = null, skip = 0, take = 0) {
    return new Promise((resolve, reject) => {
      if (typeof table === 'undefined') reject(new Error('Esperado o nome da tabela'))
      if (typeof columns === 'undefined') reject(new Error('Esperado a lista de colunas'))
      let endPoint = `/api/0.1-a/ProxyPostgRest/api/${table}?select=${columns}`
      if (filter !== null) endPoint = `${endPoint}&${filter}`
      if (order !== null) endPoint = `${endPoint}&order=${order}`
      if (skip > 0) endPoint = `${endPoint}&offset=${skip}`
      if (take > 0) endPoint = `${endPoint}&limit=${take}`
      const jsonHeaders = {
        headers: {
          'Content-Type': 'application/json',
          'Prefer': 'count=exact'
        }
      }
      this.request.get(endPoint, jsonHeaders)
        .then(res => resolve(res))
        .catch(() => reject)
    })
  }
  async rpc (fcn, body, headers = defaultJsonHeaders) {
    return new Promise((resolve, reject) => {
      if (typeof fcn === 'undefined') reject(new Error('Esperado o nome da função.'))
      const req = new Request({tag: 'postgrest'})
      req.post(`/rpc/${fcn}`, body, headers)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get estimated count
   *
   * @async
   * @function get
   * @param {string} schema - Schema name
   * @param {string} table - Table name
   * @return {Promise<json>} The json with the result
   */
  async GetEstimatedCount (schema, table) {
    return new Promise((resolve, reject) => {
      if (typeof schema === 'undefined') reject(new Error('Esperado o nome do schema'))
      if (typeof table === 'undefined') reject(new Error('Esperado o nome da tabela/view'))
      const endPoint = `/api/0.1-a/Postgres/GetEstimatedCount/${schema}/${table}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(res => resolve(res))
        .catch(() => reject)
    })
  }
  /**
   * Get a registered feature
   *
   * @async
   * @function get
   * @param {string} featureName - Feature name
   * @return {Promise<json>} The json with the result
   */
  async getRegisteredFeature (featureName) {
    return new Promise((resolve, reject) => {
      if (typeof featureName === 'undefined') reject(new Error('Esperado o nome da tabela/view'))
      const endPoint = `/api/0.1-a/Postgres/GetRegistredFeature/${featureName}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(res => resolve(res))
        .catch(() => reject)
    })
  }
}

export { Postgrest }
